import React, { useRef, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { merge } from 'ramda'
import { useSelector } from 'react-redux'

import { cpf, placa } from 'consts/regexp'
import theme, { colors } from 'theme'
import { notEmpty } from 'services/utils'
import formatter from 'services/formatter'
import { Yup } from 'services/yup'
import { toMask } from 'services/masks'
import { FormTextField } from 'components/CoForm'
import { DatePickerCollect } from 'components/DatePicker/CollectDate'
import {
  ComboboxTruckTypes,
} from 'components/Combobox'
import {
  FinishForm,
  Margin,
  StyledButton as SendButton,
  SendIcon,
} from '../styles'

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const initialData = {
  vpt_placa: '',
  id_tipo_caminhao: '',
  vpt_motorista: '',
  vpt_cod_motorista: '',
  vpt_dcoleta: '',
  capacity: 0,
  available: 0,
  tare: 0,
}

// --------------- 𝕌𝕥𝕚𝕝𝕤 ---------------

const schema = Yup.object().shape({
  vpt_placa: Yup.string().required('Informe a placa do veiculo'),
  id_tipo_caminhao: Yup.string().required('Informe o tipo do veiculo'),
  vpt_motorista: Yup.string().required('Informe o nome do motorista'),
  vpt_cod_motorista: Yup.string().required('Informe o CPF do motorista'),
  vpt_dcoleta: Yup.string().required('Informe a data de previsão'),
})

// --------------- ℂ𝕠𝕞𝕡𝕠𝕟𝕖𝕟𝕥𝕤 ---------------

export function WeightField(props) {
  return (
    <FormTextField
      {...props}
      large
      disabled
      formatValue={(v) => formatter(v).toDecimal()}
    />
  )
}

// --------------- 𝕄𝕒𝕚𝕟 ---------------

export function FormCollect({ isEditing, onSubmit }) {
  const vehicles = useSelector((state) => state.comuns.truckTypes.options)

  const activeCollectWeight = useSelector(
    (state) => state.users.collections.active.weight
  )

  const activeCollectParams = useSelector(
    (state) => state.users.collections.active.collectParams
  )

  const formRef = useRef(null)
  const availableFieldRef = useRef(null)

  const handleCollectWeightFieldColor = (available) => {
    const collectWithOvercapacity = available < 1

    formRef.current.setFieldError(
      'available',
      collectWithOvercapacity ? 'Capacidade do veículo excedida.' : null
    )

    availableFieldRef.current.style.color = collectWithOvercapacity
      ? theme.palette.error.main
      : colors.color500
  }

  const calcAvailableWeight = (capacity, tare, weight) =>
    Number(capacity - tare - weight)

  const handleSetVehicleData = useCallback(
    (plate, selectedTruckType = null) => {

      console.log(selectedTruckType)
      if (!plate) return

      const params = activeCollectParams || {}

      const tare = params?.tare || 0

      const capacity = selectedTruckType
        ? selectedTruckType.cam_pbt
        : params?.capacity || 0

      const vehicleId = selectedTruckType
        ? selectedTruckType.id_tipo_caminhao
        : params?.id_tipo_caminhao

      const available = calcAvailableWeight(
        capacity,
        tare,
        activeCollectWeight
      )

      formRef.current.setData({
        id_tipo_caminhao: vehicleId,
        capacity,
        tare,
        available,
      })

      handleCollectWeightFieldColor(available)
    },
    [activeCollectWeight, activeCollectParams]
  )

  useEffect(() => {
    if (notEmpty(vehicles)) {
      const { vpt_placa: plate, id_tipo_caminhao: vehicleId } =
        formRef.current.values

      const truckType = vehicles.find(
        (c) => c.id_tipo_caminhao === vehicleId
      )

      handleSetVehicleData(plate, truckType)
    }
  }, [activeCollectWeight, vehicles, handleSetVehicleData])

  function handleSubmit(values) {
    onSubmit(
      merge(values, {
        collectWeight: activeCollectWeight,
      })
    )
  }

  function handleAfterChangeLastVehicles(_, vehicle) {
    const plate = vehicle?.brd_placa
    handleSetVehicleData(plate)
  }

  function handleAfterChangeTruckTypes(_, truckType) {
    const plate = formRef.current.getFieldValue('vpt_placa')
    handleSetVehicleData(plate, truckType)
  }

  function handleChangeDriver(_, driver) {
    formRef.current.setFieldValue(
      'vpt_cod_motorista',
      driver ? driver.id : null
    )
  }

  return (
    <FinishForm
      formRef={formRef}
      initialData={merge(initialData, activeCollectParams)}
      onSubmit={handleSubmit}
      schema={schema}
    >
      <span className="title">
        {isEditing ? 'Editar Coleta' : 'Finalizar Coleta'}
      </span>

      <FormTextField
        label="Placa"
        name="vpt_placa"
        onAfterChange={handleAfterChangeLastVehicles}
        textMaskProps={toMask(placa.array)}
      />

      <ComboboxTruckTypes
        label="Tipo do Caminhão"
        name="id_tipo_caminhao"
        onAfterChange={handleAfterChangeTruckTypes}
      />

      <FormTextField
        label="Motorista"
        name="vpt_motorista"
        onAfterChange={handleChangeDriver}
      />

      <FormTextField
        label="CPF do Motorista"
        name="vpt_cod_motorista"
        textMaskProps={toMask(cpf.array)}
      />
      <DatePickerCollect label="Data Prevista para Coleta" name="vpt_dcoleta" />

      <FormTextField
        label="Observação"
        name="vpt_obs"
        multiline
        maxRows={10}
        minRows={5}
      />

      <Margin />

      <WeightField label="Capacidade Total do Veículo" name="capacity" />
      <WeightField label="Tara Estimada" name="tare" />

      <WeightField
        label="Peso total da Coleta"
        name="total"
        value={activeCollectWeight}
      />

      <WeightField
        label="Disponível para Carregamento"
        name="available"
        inputRef={availableFieldRef}
      />

      <Margin />

      <SendButton
        type="submit"
        label={isEditing ? 'Salvar Alterações' : 'Enviar Coleta'}
        endIcon={<SendIcon />}
      />
    </FinishForm>
  )
}

FormCollect.propTypes = {
  onSubmit: PropTypes.func,
  isEditing: PropTypes.bool,
}

FormCollect.defaultProps = {
  isEditing: false,
}

export default FormCollect
